import { atom } from 'jotai';
import request from 'lib/request';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';
import type {
  LineItemDetailResponse,
  LineItemsResponse,
} from '../types-line-item';

// RC Line Item Review data layer. Mirror of DO's atoms/interests: the overview
// tiles + per-line rows live in a MODULE-LEVEL atom (shared by the shell tree's
// section-status node and the section component, so the list is fetched once),
// with a stale-response guard; and edit / remove / restore / confirm / revert
// are uniform mutation atoms built from the SHARED buildSectionMutationAtom.

export const lineItemOverviewDataAtom = atom<LineItemsResponse | null>(null);
export const lineItemOverviewLoadingAtom = atom(false);
// Non-null means the last fetch failed — the section shows an error banner
// instead of an empty table, so a 5xx never reads as "this check has no lines"
// (this data gates catalog trust).
export const lineItemOverviewErrorAtom = atom<string | null>(null);

// The most-recent fetch target — a stale in-flight response (previous document
// or a superseded reload) must not clobber the current data (the data atom is
// module-level, shared across RC documents).
const lineItemOverviewRequestedHrefAtom = atom<string | undefined>(undefined);

export const fetchLineItemOverviewAtom = atom(
  null,
  async (get, set, href: string | undefined) => {
    if (!href) return;
    set(lineItemOverviewRequestedHrefAtom, href);
    set(lineItemOverviewLoadingAtom, true);
    set(lineItemOverviewErrorAtom, null);
    try {
      const response = await request.get<LineItemsResponse>(href);
      if (get(lineItemOverviewRequestedHrefAtom) !== href) return;
      if ('error' in response) {
        console.error('Failed to fetch RC line-item overview:', response.error);
        set(lineItemOverviewErrorAtom, 'Unable to load line items.');
      } else {
        set(lineItemOverviewDataAtom, response);
      }
    } catch (error) {
      if (get(lineItemOverviewRequestedHrefAtom) !== href) return;
      console.error('Failed to fetch RC line-item overview:', error);
      set(lineItemOverviewErrorAtom, 'Unable to load line items.');
    } finally {
      if (get(lineItemOverviewRequestedHrefAtom) === href) {
        set(lineItemOverviewLoadingAtom, false);
      }
    }
  }
);

export const lineItemOverviewUrl = (documentId: number | string) =>
  `/documents/${documentId}/royalty_check_structuring/line_items`;

// Per-line endpoint (.../line_items/:id — :id is the SF Id STRING); the edit
// PATCH target.
export const lineItemDetailUrl = (
  documentId: number | string,
  lineItemId: string
) =>
  `/documents/${documentId}/royalty_check_structuring/line_items/${lineItemId}`;

// Lifecycle / edit mutations (SHARED buildSectionMutationAtom).
export const editLineItemAtom =
  buildSectionMutationAtom<LineItemDetailResponse>();
export const removeLineItemAtom =
  buildSectionMutationAtom<LineItemDetailResponse>();
export const restoreLineItemAtom =
  buildSectionMutationAtom<LineItemDetailResponse>();
export const confirmLineItemAtom =
  buildSectionMutationAtom<LineItemDetailResponse>();
export const revertLineItemAtom =
  buildSectionMutationAtom<LineItemDetailResponse>();
